import { useEffect, useState } from 'react';

/**
 * Installare l'applicazione sulla schermata Home.
 *
 * ── perché non è un pulsante solo
 *
 * Su Android il browser annuncia che l'installazione è possibile con un evento
 * (`beforeinstallprompt`), e da lì si può aprire il pannello di sistema con un tocco. Su
 * iPhone quell'evento non esiste e non è mai esistito: Apple non l'ha implementato, e
 * l'unico modo di installare è il menù Condividi → «Aggiungi alla schermata Home». Nessuna
 * riga di codice può farlo al posto dell'utente.
 *
 * Quindi il pulsante non può essere lo stesso nei due mondi. Su Android installa davvero;
 * su iPhone può soltanto spiegare dove guardare — ed è comunque meglio di niente, perché
 * quella voce sta dentro un menù che in pochi aprono di proposito.
 *
 * ── perché la cattura sta qui e non nel componente
 *
 * `beforeinstallprompt` scatta appena il browser decide che l'applicazione è installabile,
 * cioè quasi sempre **prima** che qualcuno apra il proprio profilo. Un ascoltatore montato
 * dentro la schermata arriverebbe a cose fatte e non vedrebbe mai nulla. Si ascolta al
 * caricamento del modulo, si mette da parte l'evento e la schermata lo ritrova quando serve.
 */

interface EventoInstallazione extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let messoDaParte: EventoInstallazione | null = null;
const inAscolto = new Set<() => void>();

function avvisaTutti() {
  for (const f of inAscolto) f();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Senza questo il browser mostra la sua barra, che sulle schermate del DT coprirebbe
    // l'area di lavoro nel mezzo di un'accettazione.
    e.preventDefault();
    messoDaParte = e as EventoInstallazione;
    avvisaTutti();
  });

  window.addEventListener('appinstalled', () => {
    messoDaParte = null;
    avvisaTutti();
  });
}

/**
 * Già aperta come applicazione: dalla Home, o in una finestra senza barra degli indirizzi.
 *
 * Esportata perché serve anche all'accesso: da applicazione installata la sessione del
 * socio diventa lunga e non blocca più lo schermo. È il client a dichiararlo e il server a
 * decidere che farne — qui non c'è nessun permesso, solo un fatto sulla finestra.
 */
export function giaInstallata(): boolean {
  if (typeof window === 'undefined') return false;
  const daHome = (navigator as { standalone?: boolean }).standalone === true;
  return daHome || window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * iPhone e iPad. Su iPadOS il campo `platform` dice «MacIntel», e l'unico segnale che lo
 * distingue da un Mac è il numero di dita che lo schermo riconosce — lo stesso criterio che
 * l'applicazione usa già per capire da che dispositivo si è entrati.
 */
function suApple(): boolean {
  if (typeof navigator === 'undefined') return false;
  const p = navigator.platform ?? '';
  return /iPhone|iPod|iPad/.test(p) || (p === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);
}

export type StatoInstallazione =
  /** Già sulla Home: non c'è niente da proporre. */
  | 'installata'
  /** Android: il browser ha detto che si può, e il pannello si apre con un tocco. */
  | 'pronta'
  /** iPhone: si può, ma deve farlo la persona. Si spiega dove. */
  | 'a-mano'
  /** Desktop, o un browser che non la offre. */
  | 'non-disponibile';

export function useInstallazione(): {
  stato: StatoInstallazione;
  installa: () => Promise<void>;
} {
  const [, ridisegna] = useState(0);

  useEffect(() => {
    const f = () => ridisegna((n) => n + 1);
    inAscolto.add(f);
    return () => {
      inAscolto.delete(f);
    };
  }, []);

  const stato: StatoInstallazione = giaInstallata()
    ? 'installata'
    : messoDaParte
      ? 'pronta'
      : suApple()
        ? 'a-mano'
        : 'non-disponibile';

  async function installa() {
    if (!messoDaParte) return;
    const evento = messoDaParte;
    // Vale una volta sola: consumato, non si può riproporre. Si scarta subito, così se la
    // persona annulla il pulsante sparisce invece di restare lì a non fare più niente.
    messoDaParte = null;
    await evento.prompt().catch(() => undefined);
    avvisaTutti();
  }

  return { stato, installa };
}
